import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CRITICALITIES, MACHINE_STATUSES, newId } from '@odr/shared';

import {
  Button,
  Callout,
  Field,
  Input,
  Modal,
  Select,
  Textarea,
} from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { humanise } from '../../lib/format';

/**
 * Create or edit a piece of equipment.
 *
 * why one component for both: the fields are identical apart from the code,
 * which is the asset's permanent identity and is fixed once historical records
 * reference it. Two forms would drift, and the edit form is the one that would
 * quietly fall behind.
 */

export interface EquipmentValues {
  id: string;
  lineId: string;
  code: string;
  name: string;
  machineType: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNo: string | null;
  location: string | null;
  status: string;
  criticality: string;
  commissionDate: string | null;
  description: string | null;
}

interface LineOption {
  id: string;
  code: string;
  name: string;
  areaName: string;
  factoryName: string;
}

export function EquipmentFormModal({
  existing,
  onClose,
  onSaved,
}: {
  /** Omit to create. Provide to edit — the code becomes read-only. */
  existing?: EquipmentValues;
  onClose: () => void;
  onSaved: (machineId: string) => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = existing !== undefined;
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    lineId: existing?.lineId ?? '',
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    machineType: existing?.machineType ?? '',
    manufacturer: existing?.manufacturer ?? '',
    model: existing?.model ?? '',
    serialNo: existing?.serialNo ?? '',
    location: existing?.location ?? '',
    status: existing?.status ?? 'STOPPED',
    criticality: existing?.criticality ?? 'MEDIUM',
    commissionDate: existing?.commissionDate?.slice(0, 10) ?? '',
    description: existing?.description ?? '',
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const { data: lines } = useQuery({
    queryKey: ['org', 'lines'],
    queryFn: () => api.get<{ data: LineOption[] }>('/api/org/lines'),
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        lineId: form.lineId,
        name: form.name.trim(),
        machineType: form.machineType.trim() || null,
        manufacturer: form.manufacturer.trim() || null,
        model: form.model.trim() || null,
        serialNo: form.serialNo.trim() || null,
        location: form.location.trim() || null,
        status: form.status,
        criticality: form.criticality,
        commissionDate: form.commissionDate || null,
        description: form.description.trim() || null,
      };

      if (isEdit) {
        // The code is deliberately absent: it is printed on the asset and
        // referenced by every historical record.
        await api.patch(`/api/machines/${existing.id}`, body);
        return existing.id;
      }

      // Client-minted id, consistent with the rest of ODR (ADR-0001).
      const id = newId();
      await api.post('/api/machines', { ...body, id, code: form.code.trim().toUpperCase() });
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries();
      onSaved(id);
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? caught.message : 'That equipment could not be saved.',
      ),
  });

  const codeValid = isEdit || /^[A-Za-z0-9][A-Za-z0-9._-]{1,39}$/.test(form.code.trim());
  const ready = form.lineId !== '' && form.name.trim().length > 1 && codeValid;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${existing.code}` : 'Add equipment'}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            {isEdit ? 'Save changes' : 'Add equipment'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}

        {isEdit ? (
          <Callout tone="info" title={`Equipment code stays ${existing.code}`}>
            The code is printed on the asset and every checklist run, Problem Tag and OPL
            refers to it. To take equipment out of service, retire it rather than renaming it.
          </Callout>
        ) : null}

        <Field
          label="Line"
          required
          hint="Which production line this equipment sits on. Decides who sees it."
        >
          <Select value={form.lineId} onChange={(e) => set('lineId')(e.target.value)}>
            <option value="">Choose a line…</option>
            {lines?.data.map((line) => (
              <option key={line.id} value={line.id}>
                {line.factoryName} · {line.areaName} · {line.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Equipment code"
            required
            hint={
              isEdit
                ? 'Fixed once created.'
                : 'Printed on the asset, e.g. PACK-03. Must be unique.'
            }
          >
            <Input
              value={form.code}
              disabled={isEdit}
              placeholder="PACK-03"
              onChange={(e) => set('code')(e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Name" required hint="What people on the line call it.">
            <Input
              value={form.name}
              placeholder="Sachet Sealer"
              onChange={(e) => set('name')(e.target.value)}
            />
          </Field>
        </div>

        {!isEdit && form.code.trim() !== '' && !codeValid ? (
          <Callout tone="warn">
            Use letters, numbers, dots, dashes or underscores — at least two characters.
          </Callout>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Status">
            <Select value={form.status} onChange={(e) => set('status')(e.target.value)}>
              {MACHINE_STATUSES.filter((s) => s !== 'RETIRED').map((s) => (
                <option key={s} value={s}>
                  {humanise(s)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Criticality" hint="How much it matters when this stops.">
            <Select value={form.criticality} onChange={(e) => set('criticality')(e.target.value)}>
              {CRITICALITIES.map((c) => (
                <option key={c} value={c}>
                  {humanise(c)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Equipment type">
            <Input
              value={form.machineType}
              placeholder="Filling / Packing"
              onChange={(e) => set('machineType')(e.target.value)}
            />
          </Field>
          <Field label="Location">
            <Input
              value={form.location}
              placeholder="Hall 2, bay 4"
              onChange={(e) => set('location')(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Manufacturer">
            <Input
              value={form.manufacturer}
              onChange={(e) => set('manufacturer')(e.target.value)}
            />
          </Field>
          <Field label="Model">
            <Input value={form.model} onChange={(e) => set('model')(e.target.value)} />
          </Field>
          <Field label="Serial number">
            <Input value={form.serialNo} onChange={(e) => set('serialNo')(e.target.value)} />
          </Field>
        </div>

        <Field label="Commissioned">
          <Input
            type="date"
            value={form.commissionDate}
            onChange={(e) => set('commissionDate')(e.target.value)}
          />
        </Field>

        <Field label="Notes" hint="Anything an operator or engineer should know.">
          <Textarea
            rows={3}
            value={form.description}
            onChange={(e) => set('description')(e.target.value)}
          />
        </Field>

        {!isEdit ? (
          <Callout tone="info" title="What happens next">
            A QR label is generated automatically so operators can scan straight to this
            equipment. Add its components, then schedule a checklist against it — until a
            schedule exists, nothing is due and it will not appear in compliance.
          </Callout>
        ) : null}
      </div>
    </Modal>
  );
}
